import React from 'react';
import Helmet from 'react-helmet';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { List, Button, Row, Col, Collapse, Modal, Upload, message, Progress, Tag } from 'antd';
import { LoadingSpinner } from './LoadingSpinner';
import app_params from '../../../config/params';
import {
  SyncOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  CloseOutlined,
  WarningOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { analytics } from '../modules/analytics';
// import { pageviewGA, eventGA } from '../modules/ganalytics';


const { Dragger } = Upload;
const { Panel } = Collapse;

export class GalleryMap extends React.Component {
  static propTypes = {
    gallery: PropTypes.shape({
      medias: PropTypes.array.isRequired,
      status: PropTypes.string,
      mode: PropTypes.string.isRequired,
      selectedIds: PropTypes.array.isRequired,
    }).isRequired,

    token: PropTypes.string.isRequired,
    networkError: PropTypes.bool.isRequired,

    getMedias: PropTypes.func.isRequired,
    deleteMedia: PropTypes.func.isRequired,
    clickMedia: PropTypes.func.isRequired,
    exitSelectMode: PropTypes.func.isRequired,
    changeUploadStatus: PropTypes.func.isRequired,
  };

  componentDidMount() {
    analytics.trackView('/gallery');
    if (this.props.gallery.status === 'init') {
      this.props.getMedias();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.networkError && !this.props.networkError) {
      this.props.getMedias();
    }
  }

  onDraggerChange = (info) => {
    this.props.changeUploadStatus(info);

    const { status } = info.file;
    if (status === 'done') {
      // eventGA({
      //   category: 'gallery',
      //   action: 'gallery_photo_upload_success',
      // });
      message.success(`${info.file.name} file uploaded successfully.`);
    }
    else if (status === 'error') {
      // eventGA({
      //   category: 'gallery',
      //   action: 'gallery_photo_upload_fail',
      // });
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  handleCardClick = (mediaUrlsafe) => {
    if (typeof mediaUrlsafe !== 'undefined') {
      this.props.clickMedia(mediaUrlsafe);
    }
  };

  exitSelectBtnClicked = () => {
    this.props.exitSelectMode();
  };

  refreshBtnClicked = () => {
    this.props.getMedias();
  };

  deleteBtnClicked = () => {
    Modal.confirm({
      icon: <WarningOutlined />,
      title: 'Are you sure?',
      content: 'Deleting photos from your gallery will also remove them from pages.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Never mind',
      maskClosable: true,
      onOk: (() => {
        // eventGA({
        //   category: 'gallery',
        //   action: 'gallery_photo_delete',
        // });
        this.props.deleteMedia(this.props.gallery.selectedIds);
      }),
    });
  };

  renderHeaderBar = (mode, selectedIds, medias) => {
    const numPhotos = medias.length;
    const photoStr = (medias.length > 1) ? 'photos' : 'photo';

    const headerRefreshBtn = (
      <Button type="link" onClick={this.refreshBtnClicked} icon={<SyncOutlined />} className="px-0">
        Refresh
      </Button>
    );

    const headerDeleteBtn = (
      <Button
        type="danger"
        icon={<DeleteOutlined />}
        onClick={this.deleteBtnClicked}
      >
        Delete selected
      </Button>
    );

    const headerExitSelectBtn = (
      <Button
        type="link"
        icon={<CloseOutlined />}
        shape="circle"
        className="ml-3"
        onClick={this.exitSelectBtnClicked}
      />
    );

    if (numPhotos === 0) {
      return null;
    }

    return (
      <div className="mt-4 header__bar d-flex align-items-center justify-content-between">
        <div className="header__text">
          { (mode === 'preview') ? (
            <div>Showing {numPhotos} {photoStr}</div>
          ) : (
            <div>{ selectedIds.length } of {medias.length} selected</div>
          )
          }
        </div>
        <div>
          {
            (mode === 'select') ? (
              <span>
                { headerDeleteBtn }
                { headerExitSelectBtn }
              </span>
            ) : (
              <span>
                { headerRefreshBtn }
              </span>
            )
          }
        </div>
      </div>
    );
  };

  renderItem = (item) => {
    const { thumbUrl } = item;


    let imgNode = null;
    if (thumbUrl && thumbUrl.length > 1) {
      // client side preview in base64 available only during uploads

      imgNode = <img alt={item.name} src={thumbUrl} />;
    }
    else {
      if ('resolutions' in item &&
        'thumb' in item.resolutions &&
        item.resolutions.thumb !== null &&
        'path' in item.resolutions.thumb &&
        item.resolutions.thumb.path !== null
      ) {
        const thumbPath = `${app_params.GAE_ENDPOINT.MEDIA_URL}${item.resolutions.thumb.path}`;
        imgNode = <img alt={item.name} src={thumbPath} />;
      }
    }

    if (imgNode === null) { // fallback (usually when clientside preview could not be generated)
      imgNode = (
        <div className="bg-white d-flex align-items-center justify-content-center" style={{ height: 100 }}>
          <LoadingOutlined className="text-muted" style={{fontSize: 22}}/>
        </div>
      );
    }

    let max_resolution = '200';
    if ('resolutions' in item) {
      const { resolutions } = item;
      if ('small' in resolutions && resolutions.small !== null) {
        max_resolution = '640';
      }
      if ('medium' in resolutions && resolutions.medium !== null) {
        max_resolution = '1280';
      }
      if ('large' in resolutions && resolutions.large !== null) {
        max_resolution = '1920';
      }
    }

    return (
      <List.Item key={item.id}>
        <div
          className={cx(
            'gallery__item mb-4 rounded text-center overflow-hidden',
            { selected: item.selected },
            { shadow: !item.selected },
          )}
          onClick={() => this.handleCardClick(item.urlsafe)}
        >
          { item.percent ? null : (
            <Tag className="size_tag">{max_resolution}</Tag>
          )}
          { imgNode }
          <div className="extra py-2 px-3">
            { (item.percent) ? (
                <Progress percent={item.percent} showInfo={false} style={{ marginTop: 4 }} />
              ) : (
                <div className="gallery_label text-dark text-left">{item.name}</div>
              )
            }
          </div>
        </div>
      </List.Item>
    );
  }

  render() {
    const { token } = this.props;
    const { medias, status, mode, selectedIds } = this.props.gallery;
    const myMedias = medias.filter(m => !('sample' in m));
    const isLoading = (status === 'loading');
    const isEmpty = (myMedias.length === 0);

    const emptyState = (
      <div className="text-center">
        <Button
          type="secondary"
          icon={<SyncOutlined />}
          onClick={this.refreshBtnClicked}
          className="my-5 mx-auto"
        >
          Reload Gallery
        </Button>

        <h5 className="mb-3">FAQs</h5>
        <Row>
          <Col
            xs={{ span: 24, offset: 0 }}
            sm={{ span: 24, offset: 0 }}
            md={{ span: 18, offset: 3 }}
            lg={{ span: 16, offset: 4 }}
            xl={{ span: 12, offset: 6 }}
          >
            <Collapse accordion className="text-left">
              <Panel header="What is the purpose of the media gallery?" key="1">
                <p>
                  The media gallery is where you can view the collective repository of all your images that are used
                  in all of your landing pages put together. It helps with collaborating on media assets with the entire team.
                </p>
                <p>
                  The media gallery can also be accessed from within the design editor later on when you’re designing a page.
                </p>
              </Panel>
              <Panel header="Is there a restriction on the photos I can upload to my gallery?" key="2">
                <p>
                  Images you upload must be either jpg, progressive jpg or png, and must be less than 10MB per image.
                </p>
                <p>
                  Also, it is your and not PageHits’s responsibility to ensure you own the right to images you upload.
                </p>
              </Panel>
              <Panel header="Can I upload videos?" key="3">
                <p>
                  Currently we support uploading only photos and no videos.
                  However, if you want to use videos in your landing pages,
                  You can always embed Youtube clips.
                </p>
              </Panel>
            </Collapse>
          </Col>
        </Row>
      </div>
    );

    const mediaUploadUrl = `${app_params.GAE_ENDPOINT.BASE_URL}api/media/upload`;

    const uploadDragger = (
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 mx-auto p-0">
            <Dragger
              className="bg-white rounded"
              name="file"
              multiple={true}
              listType="picture"
              onChange={this.onDraggerChange}
              action={mediaUploadUrl}
              headers={{ Authorization: token }}
              showUploadList={true}
            >
              <CloudUploadOutlined className="text-muted" style={{ fontSize: 48 }} />
              <h5 className="my-3">Click or drag photos here to upload</h5>
              <div className="small text-muted">Uploaded files are subject to copyright terms</div>
            </Dragger>

          </div>
        </div>
      </div>
    );

    const galleryHeader = this.renderHeaderBar(mode, selectedIds, myMedias);
    const galleryItems = (
      <List
        className="gallery__item__wrap"
        itemLayout="vertical"
        grid={{ gutter: 18, xs: 2, sm: 3, md: 3, lg: 4, xl: 4, xxl: 4 }}
        dataSource={myMedias}
        type="flex" justify="center" align="top"
        renderItem={this.renderItem}
      />
    );

    let content = null;
    if (isLoading) {
      content = <LoadingSpinner />;
    }
    else {
      content = (
        <React.Fragment>
          { uploadDragger }
          { galleryHeader }
          <div className="w-100" />
          <div className="mt-0">
            { isEmpty ? (
              emptyState
            ) : (
              galleryItems
            )}
          </div>
        </React.Fragment>
      );
    }

    return (
      <div className="app__gallery" >
        <Helmet>
          <title>Gallery</title>
        </Helmet>
        { content }
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./scripts/presentation/GalleryMap.jsx